/**
 * Иллюстрации страниц кейсов — данные (вид — src/components/CaseVisual.tsx,
 * типы — src/data/case-visual-types.ts).
 *
 * Правило Тони 2026-10-21: «во всех кейсах нужны иллюстрации, которые
 * отображают суть и делают кейс осязаемым при чтении». Страж —
 * tests/e2e/case-pages.spec.ts: у каждого кейса на обоих языках не меньше
 * двух иллюстраций, и все из данных действительно видны на странице.
 *
 * Источник цифр — тот же, что у текста кейса (src/data/case-studies.ts):
 * ничего нового, ничего не пересчитано в нашу пользу. Для заборов — ещё и
 * макеты, которые прислал Тони 21.10 (12 PNG): из них взяты
 * запросы к ИИ и диалог ИИ-менеджера; название клиента не переносится (NDA).
 *
 * В русских строках пробелы обычные — неразрывные расставит nbspDeep.
 */
import type { Locale } from '@/i18n/locale'
import type { CaseId } from '@/lib/case-slugs'
import { nbspDeep } from '@/lib/typo'
import type { CaseVisual } from './case-visual-types'

type Visuals = Record<Locale, CaseVisual[]>

/* ————————————————————— Производитель заборов ————————————————————— */

const FENCES: Visuals = {
  ru: [
    {
      slot: 'lead',
      kind: 'timeline',
      kicker: 'Хронология внедрения',
      title: 'Пять недель: от ключей к ночной смене',
      ask: 'Собери итог пяти недель внедрения — для собственника.',
      steps: [
        { when: 'Неделя 1', title: 'Контур', text: 'Workspace, единый вход, коннекторы к CRM, Авито и 1С' },
        { when: '22 сентября', title: 'Сверка финансов', text: '«−1,5 млн» оказались 13,7 млн ₽ в компании' },
        { when: '7 октября', title: 'Цены от себестоимости', text: '636 вариантов — ни одного в минусе' },
        { when: '9 октября', title: 'Разбор менеджеров', text: 'все звонки дня — к 9:00' },
        { when: '20 октября', title: 'Ночная смена', text: 'ИИ-менеджер в чатах Авито' },
      ],
    },
    {
      slot: 'pointA',
      kind: 'transform',
      kicker: 'Контур компании · неделя 1',
      title: 'Ключи — у владельца',
      ask: 'У кого сейчас ключи от сайта, домена и почты?',
      legend: ['Было', 'Стало'],
      rows: [
        {
          label: 'Кто держит ключи',
          before: 'Сайтом и доменом управлял подрядчик, процессы жили на личной почте',
          after: 'Главный администратор корпоративного контура — владелец',
        },
        {
          label: 'Почта',
          before: 'Корпоративной почты не было',
          after: 'Почта на домене компании — без смены адреса',
        },
        {
          label: 'Уход сотрудника',
          before: 'Менеджер ушла с доступами — пароли меняли вручную',
          after: 'Доступ выдаётся и отзывается одной кнопкой',
        },
        {
          label: 'Данные',
          before: 'Остаются у человека, который ушёл',
          after: 'Остаются у компании',
        },
      ],
    },
    {
      slot: 'done',
      kind: 'systems',
      kicker: 'Подключённые системы',
      title: 'Семь систем — без выгрузок',
      ask: 'Покажи, что подключено и что тебе можно делать.',
      hub: 'Claude · корпоративный контур',
      nodes: [
        { name: 'amoCRM', does: 'Воронка, отказы, звонки, сметы, запись на замер, переписка', mode: 'запись после подтверждения', access: 'confirm' },
        { name: 'Авито · ~55 функций', does: '324 объявления, расходы, чаты, отзывы, ставки, конкуренты', mode: 'изменения с журналом и откатом', access: 'confirm' },
        { name: '1С: УНФ и Бухгалтерия', does: 'Документы, проводки, остатки; отдельный пользователь «ИИ-аналитик»', mode: 'только чтение', access: 'read' },
        { name: 'Калькулятор сайта', does: 'Расчёт любого забора и ворот так, как его видит клиент', mode: 'расчёт', access: 'read' },
        { name: 'График монтажей', does: '701 монтаж, 668 связаны со сделками; бригады и материалы', mode: 'только чтение', access: 'read' },
        { name: 'Google Workspace', does: 'Почта, календарь, Диск, база знаний, портфолио', mode: 'чтение и запись', access: 'write' },
        { name: 'Браузер', does: 'Админка сайта и веб-кабинеты', mode: 'с подтверждением', access: 'confirm' },
      ],
      rules: [
        'Писать клиентам — только по сделкам с отдельным тегом',
        'Перед правкой цен — копия 1 100+ полей, откат за минуты',
        'Телефоны клиентов скрыты, каждое действие — в журнале',
      ],
    },
    {
      slot: 'done',
      kind: 'memo',
      kicker: 'Разбор дня · каждый менеджер',
      title: 'К 9:00 руководитель продаж знает, что исправить',
      listTitle: 'Что повторяется в звонках',
      items: [
        'Клиент спрашивает «когда приедете?» — а двух конкретных дат ему не предлагают',
        'Скидка звучит раньше, чем клиент возразил по цене',
        '«От 30 000» в объявлении рушится в первом же звонке',
        'Обещания расходятся с договором — поэтому собран единый свод условий',
      ],
      quoteTitle: 'Готовое сообщение клиенту',
      quote: 'Здравствуйте! По вашему забору: замерщик может приехать в четверг с 10 до 12 или в субботу с 14 до 16. Какой вариант удобнее?',
      quoteNote: 'Сверка сметы: 176 300 ₽ в CRM расходились с калькулятором на 6 300 ₽ — внутри нашёлся недобор 1 000 ₽: столбы 100×100 посчитаны по цене 80×80.',
    },
    {
      slot: 'done',
      kind: 'dialog',
      kicker: 'ИИ-менеджер · тест с руководителем продаж',
      title: 'Разговор всегда заканчивается датой замера',
      ask: 'Как прошла первая ночь ИИ-менеджера?',
      messages: [
        { side: 'client', who: 'Клиент · роль играет руководитель продаж', text: 'Нужны откатные ворота с автоматикой. Сколько?' },
        { side: 'ai', who: 'ИИ-менеджер', text: 'Уточню два момента: ширина проёма и какое заполнение — профнастил или 3D-панель? Посчитаю под ключ.' },
        { side: 'client', who: 'Клиент', text: 'Дороговато. У конкурента дешевле.' },
        {
          side: 'ai',
          who: 'ИИ-менеджер',
          text: 'Пересчитал под ваш запрос: 135 680 ₽. Без автоматики — 107 180 ₽. Давайте сравним комплектации по позициям: рама, автоматика, гарантия. Замерщик может приехать в четверг или пятницу.',
        },
      ],
      chips: ['144 080 ₽', '137 180 ₽', '135 680 ₽', 'без автоматики 107 180 ₽'],
      asideTitle: 'Правила собственника',
      aside: [
        'Сначала 2–3 уточняющих вопроса, потом цена',
        'Рама откатных по умолчанию — 60×40',
        'На «дорого» — сравнение комплектаций, а не новая скидка',
        'Никаких ложных обещаний вроде «передам за 15 минут»',
      ],
      note: 'С 20 октября ИИ-менеджер закрывает чаты Авито с 21:00 до 9:00 под контролем руководителя продаж. В первую ночь он продолжил разговор менеджера без расхождений в ценах и корректно промолчал там, где ждали ответа клиента.',
    },
    {
      slot: 'findings',
      kind: 'tiles',
      kicker: 'Рентген бизнеса · подтверждено собственником',
      title: 'Что теперь известно о компании — в цифрах',
      ask: 'Что мы теперь знаем о бизнесе — цифрами?',
      tiles: [
        { label: 'Заявка → замер', value: '16 %', text: 'из замера в договор — 91 %: узкое место — первый контакт', meter: 16, tone: 'loss' },
        { label: 'Загрузка бригад', value: '87 %', text: 'летом 110–119 %, весной и в сентябре 35–80 %: 189 свободных бригадо-дней', meter: 87, tone: 'accent' },
        { label: 'Цех', value: '68 %', text: 'гипотеза «сварщики — узкое место» опровергнута', meter: 68, tone: 'accent' },
        { label: 'Замерщик', value: '46 %', text: 'второй замерщик пока не нужен', meter: 46, tone: 'accent' },
        { label: 'Накладные', value: '41 904 ₽', text: 'на каждый день работы бригады' },
        { label: 'Реклама', value: '7,2 млн ₽', text: 'в год; заявка — 1 720 ₽, сделка с Авито вдвое дешевле Директа' },
        { label: 'Цены сайта', value: 'до −41 %', text: 'рабица в минус; длинные заборы — до −5 %', tone: 'loss' },
        { label: 'Потолок роста', value: '~10 бригад', text: 'налоговый режим, длинная «Газель» и замер; цель «15» — на 2028 год' },
        { label: 'Учёт в 1С', value: '62 955', text: 'проводок с мая 2024 года, 45 из них датированы будущим' },
      ],
      note: 'Две сверки сошлись сами: 7,2 млн ₽ рекламы ÷ 1 720 ₽ = 4 186 заявок при «около 4 200» у компании; расходы на Авито — 3,3 % от суммы договоров канала при независимой оценке «около 3,8 %».',
    },
    {
      slot: 'findings',
      kind: 'bars',
      kicker: 'Воронка · сентябрь, 582 заявки',
      title: 'Где теряются заявки: сколько дошло до замера',
      ask: 'Где мы теряем клиентов? Только честно, после исправления учёта.',
      max: 70,
      rows: [
        { label: 'Авито, чат', sub: '208 заявок', value: 8, text: '8 %', note: 'молчание: 70 из 104 таких отказов', tone: 'loss' },
        { label: 'Звонок на основной номер', sub: '98 заявок', value: 18, text: '18 %', note: '«сами свяжутся» без назначенного контакта' },
        { label: 'Звонок Авито', sub: '89 заявок', value: 19, text: '19 %', note: '«сами свяжутся» — 17 отказов' },
        { label: 'Сайт и калькулятор', sub: '48 заявок', value: 29, text: '29 %', note: 'цена' },
        { label: 'Отдельный рекламный номер', sub: '44 заявки', value: 0, text: '0 %', note: 'спам — 43 из 44', tone: 'loss' },
        { label: 'Сарафан', sub: '19 заявок', value: 63, text: '63 %', tone: 'accent' },
      ],
      note: 'Сентябрьские заявки ещё в работе: доли сравнимы между каналами, но выше годовых 16 %. Мусор — 160 из 420 отказов, цена — около четверти, ровно по всем каналам.',
    },
    {
      slot: 'findings',
      kind: 'columns',
      kicker: 'Аудит калькулятора · стоимость работ',
      title: 'На границе длины цена обваливалась: 91 метр дешевле 90',
      ask: 'Проверь калькуляторы сайта против реальных сделок.',
      flag: '−34 654 ₽ за лишний метр',
      rows: [
        { label: '90 метров', value: 159176, text: '159 176 ₽', tone: 'base' },
        { label: '91 метр', value: 124522, text: '124 522 ₽', tone: 'loss' },
      ],
      note: 'И 100 метров выходили дешевле 90. Границы длины вынесены в настройки и проверены во всех калькуляторах; минимум монтажа пересчитан от себестоимости, швеллер поднят с 1 500 до 2 800 ₽ за метр.',
    },
    {
      slot: 'results',
      kind: 'compare',
      kicker: 'Цены от себестоимости · с 7 октября',
      title: 'Маржа на бригадо-день: 3D-панели',
      ask: 'Что дают новые цены на заказах этого года?',
      legend: ['Было', 'Стало'],
      scale: 'shared',
      rows: [
        { label: '10 метров', before: 7800, after: 18600, beforeText: '7 800 ₽', afterText: '18 600 ₽' },
        { label: '30 метров', before: 12300, after: 23000, beforeText: '12 300 ₽', afterText: '23 000 ₽' },
        { label: '100 метров', before: 16900, after: 21800, beforeText: '16 900 ₽', afterText: '21 800 ₽' },
      ],
      note: 'Планка — не ниже 20 000 ₽ на бригадо-день. Менялись ставки и минимумы установки, цены материалов не трогали.',
    },
    {
      slot: 'results',
      kind: 'compare',
      kicker: 'Прибыль на тех же заказах · январь–сентябрь · расчёт',
      title: '+2,4 % к цене — это +24 % к прибыли',
      legend: ['Старые', 'Новые'],
      rows: [
        { label: 'Остаётся в компании', before: 13.7, after: 17.0, beforeText: '13,7 млн ₽', afterText: '17,0 млн ₽', delta: '+3,3 млн ₽' },
      ],
      note: 'Прибыль — около десятой части оборота, а наценка ни с кем не делится и уходит в неё целиком: отсюда плечо в десять раз. Расчёт на фактических заказах при том же числе клиентов; проверяем фактом весной 2027 года.',
    },
    {
      slot: 'results',
      kind: 'columns',
      kicker: 'Авито · сквозная аналитика до суммы договора',
      title: 'Цена контакта упала почти втрое',
      ask: 'Авито окупается? Покажи сквозную аналитику до суммы договора.',
      rows: [
        { label: 'Июль', value: 1095, text: '1 095 ₽' },
        { label: 'Август', value: 716, text: '716 ₽' },
        { label: 'Сентябрь', value: 382, text: '382 ₽' },
        { label: 'Октябрь, 1–12', value: 569, text: '569 ₽', tone: 'base' },
      ],
      note: 'Расход на один договор — 8 850 → 3 487 ₽, привязка сделок к объявлениям — 11 → 52 %. Снижение ИИ не приписываем: новые ставки пока только подготовлены, октябрь не дозрел.',
    },
    {
      slot: 'method',
      kind: 'verify',
      kicker: 'Сверка финансов · январь–сентябрь',
      title: 'Первая цифра была неверной',
      ask: 'Первая оценка показала минус полтора миллиона. Это правда?',
      wrong: { label: 'Первая оценка · 22 сентября', value: '−1,5 млн ₽', text: 'Неполные данные: накладные висели черновиками, расходы двух ИП не сводились' },
      right: { label: 'Проверенная цифра', value: '13,7 млн ₽', text: 'остаётся в компании за январь–сентябрь, около 15,5 млн ₽ за 12 месяцев — после зарплаты собственника' },
      checksTitle: 'Четыре независимых источника выручки',
      checks: ['Продажи', 'Отчёты о прибылях', 'Поступления', 'Закупки'],
      ok: 'сходится',
      note: 'Больше 40 выгрузок из 1С и CRM за год. На грязных данных ИИ выдаёт убедительный и неверный ответ: сначала проверяется, где лежат данные, и только потом им верят.',
    },
    {
      slot: 'next',
      kind: 'ledger',
      kicker: 'Эффект внедрения',
      title: 'Сделанное — отдельно от расчётов',
      ask: 'Сведи эффект. Отдели сделанное от расчётов — без приукрашивания.',
      rows: [
        { tag: 'Сделано', tone: 'done', what: 'Все 636 вариантов калькулятора не ниже нуля после всех расходов', amount: '—' },
        { tag: 'Расчёт', tone: 'calc', what: 'Те же заказы января–сентября по новым ценам', amount: '+3,3 млн ₽ за 9 мес' },
        { tag: 'Расчёт', tone: 'calc', what: 'Ноябрь–декабрь: длинные заборы с поправкой на короткий день вместо минуса до −13 %', amount: '≈ +0,6 млн ₽' },
        { tag: 'Потенциал', tone: 'potential', what: '«Заявка Авито → замер» с 12 до 20 % при том же бюджете', amount: '≈ ×1,5 договоров' },
        { tag: 'Потенциал', tone: 'potential', what: '189 свободных бригадо-дней — ранняя бронь сезона', amount: 'нужна сверка методики' },
        { tag: 'Цель 2027', tone: 'goal', what: '10 бригад к июню', amount: '≈30 млн ₽ против 15,5 млн ₽' },
      ],
    },
  ],
  en: [
    {
      slot: 'lead',
      kind: 'timeline',
      kicker: 'Implementation timeline',
      title: 'Five weeks: from the keys to the night shift',
      ask: 'Sum up the five weeks of the rollout — for the owner.',
      steps: [
        { when: 'Week 1', title: 'Environment', text: 'Workspace, single sign-on, connectors to the CRM, Avito and 1C' },
        { when: 'September 22', title: 'Financial check', text: 'the “−₽1.5M” turned out to be ₽13.7M left in the company' },
        { when: 'October 7', title: 'Prices from cost', text: '636 options — none in the red' },
        { when: 'October 9', title: 'Manager review', text: 'every call of the day — by 9:00' },
        { when: 'October 20', title: 'Night shift', text: 'an AI manager in Avito chats' },
      ],
    },
    {
      slot: 'pointA',
      kind: 'transform',
      kicker: 'Company environment · week 1',
      title: 'The keys belong to the owner',
      ask: 'Who holds the keys to the website, the domain and email right now?',
      legend: ['Before', 'After'],
      rows: [
        {
          label: 'Who holds the keys',
          before: 'A contractor controlled the website and the domain; work lived in personal mailboxes',
          after: 'The owner is the super administrator of the corporate environment',
        },
        { label: 'Email', before: 'There was no corporate email', after: 'Email on the company domain — same address' },
        {
          label: 'When someone leaves',
          before: 'A manager left with the access — passwords were changed by hand',
          after: 'Access is granted and revoked with one button',
        },
        { label: 'Data', before: 'Stays with the person who left', after: 'Stays with the company' },
      ],
    },
    {
      slot: 'done',
      kind: 'systems',
      kicker: 'Connected systems',
      title: 'Seven systems — no exports',
      ask: 'Show me what is connected and what you are allowed to do.',
      hub: 'Claude · corporate environment',
      nodes: [
        { name: 'amoCRM', does: 'Funnel, lost deals, calls, estimates, survey booking, chats', mode: 'writes after confirmation', access: 'confirm' },
        { name: 'Avito · ~55 functions', does: '324 listings, spend, chats, reviews, bids, competitors', mode: 'changes with log and rollback', access: 'confirm' },
        { name: '1C: operations and accounting', does: 'Documents, postings, balances; a separate “AI analyst” user', mode: 'read-only', access: 'read' },
        { name: 'Website calculator', does: 'Prices any fence or gate the way the customer sees it', mode: 'calculation', access: 'read' },
        { name: 'Installation schedule', does: '701 installations, 668 linked to deals; crews and materials', mode: 'read-only', access: 'read' },
        { name: 'Google Workspace', does: 'Email, calendar, Drive, knowledge base, portfolio', mode: 'read and write', access: 'write' },
        { name: 'Browser', does: 'Website admin and web dashboards', mode: 'with confirmation', access: 'confirm' },
      ],
      rules: [
        'Messages to customers only for deals with a dedicated tag',
        'Before any price change — a copy of 1,100+ fields, rollback in minutes',
        'Customer phone numbers hidden, every action logged',
      ],
    },
    {
      slot: 'done',
      kind: 'memo',
      kicker: 'Daily review · every manager',
      title: 'By 9:00 the head of sales knows what to fix',
      listTitle: 'What keeps repeating in calls',
      items: [
        'The customer asks “when can you come?” — and is not offered two specific dates',
        'A discount comes up before the customer objects to the price',
        '“From 30,000” in the listing falls apart in the very first call',
        'Promises differ from the contract — so one set of terms was written',
      ],
      quoteTitle: 'Ready message to the customer',
      quote: 'Hello! About your fence: the surveyor can come on Thursday from 10 to 12 or on Saturday from 14 to 16. Which suits you better?',
      quoteNote: 'Estimate check: ₽176,300 in the CRM differed from the calculator by ₽6,300 — inside was a ₽1,000 shortfall: 100×100 posts priced as 80×80.',
    },
    {
      slot: 'done',
      kind: 'dialog',
      kicker: 'AI manager · test with the head of sales',
      title: 'A conversation always ends with a survey date',
      ask: 'How did the AI manager’s first night go?',
      messages: [
        { side: 'client', who: 'Customer · played by the head of sales', text: 'I need a sliding gate with automation. How much?' },
        { side: 'ai', who: 'AI manager', text: 'Two quick questions: how wide is the opening, and which infill — profiled sheet or 3D panel? I’ll price it turnkey.' },
        { side: 'client', who: 'Customer', text: 'A bit pricey. A competitor is cheaper.' },
        {
          side: 'ai',
          who: 'AI manager',
          text: 'Recalculated for your request: ₽135,680. Without automation — ₽107,180. Let’s compare the packages item by item: frame, automation, warranty. The surveyor can come on Thursday or Friday.',
        },
      ],
      chips: ['₽144,080', '₽137,180', '₽135,680', 'no automation ₽107,180'],
      asideTitle: 'The owner’s rules',
      aside: [
        'First 2–3 clarifying questions, then the price',
        'Default sliding-gate frame — 60×40',
        'On “too expensive” — compare packages, not a new discount',
        'No false promises like “I’ll pass it on in 15 minutes”',
      ],
      note: 'Since October 20 the AI manager covers Avito chats from 21:00 to 9:00 under the head of sales’ supervision. On the first night it continued a manager’s conversation with no price mismatch and stayed silent where a customer’s reply was pending.',
    },
    {
      slot: 'findings',
      kind: 'tiles',
      kicker: 'Business X-ray · confirmed by the owner',
      title: 'What is now known about the company — in numbers',
      ask: 'What do we now know about the business — in numbers?',
      tiles: [
        { label: 'Lead → survey', value: '16%', text: '91% of surveys become contracts: the bottleneck is the first contact', meter: 16, tone: 'loss' },
        { label: 'Crew load', value: '87%', text: '110–119% in summer, 35–80% in spring and September: 189 free crew-days', meter: 87, tone: 'accent' },
        { label: 'Workshop', value: '68%', text: 'the “welders are the bottleneck” hypothesis is disproved', meter: 68, tone: 'accent' },
        { label: 'Surveyor', value: '46%', text: 'a second surveyor is not needed yet', meter: 46, tone: 'accent' },
        { label: 'Overhead', value: '₽41,904', text: 'for every crew working day' },
        { label: 'Advertising', value: '₽7.2M', text: 'a year; ₽1,720 per lead, an Avito deal costs half as much as Yandex Direct' },
        { label: 'Website prices', value: 'down to −41%', text: 'chain-link at a loss; long fences down to −5%', tone: 'loss' },
        { label: 'Growth ceiling', value: '~10 crews', text: 'tax regime, the long GAZelle truck and surveys; the goal of “15” moves to 2028' },
        { label: '1C records', value: '62,955', text: 'postings since May 2024, 45 of them dated in the future' },
      ],
      note: 'Two cross-checks matched on their own: ₽7.2M of advertising ÷ ₽1,720 = 4,186 leads against the company’s “about 4,200”; Avito spend is 3.3% of the channel’s contract total against an independent “about 3.8%”.',
    },
    {
      slot: 'findings',
      kind: 'bars',
      kicker: 'Funnel · September, 582 leads',
      title: 'Where leads are lost: how many reached a site survey',
      ask: 'Where are we losing customers? Be honest — after the CRM fix.',
      max: 70,
      rows: [
        { label: 'Avito chat', sub: '208 leads', value: 8, text: '8%', note: 'silence: 70 of 104 such losses', tone: 'loss' },
        { label: 'Call to the main number', sub: '98 leads', value: 18, text: '18%', note: '“they will get back to us” with no next contact set' },
        { label: 'Avito call', sub: '89 leads', value: 19, text: '19%', note: '“they will get back to us” — 17 losses' },
        { label: 'Website and calculator', sub: '48 leads', value: 29, text: '29%', note: 'price' },
        { label: 'Separate ad number', sub: '44 leads', value: 0, text: '0%', note: 'spam — 43 of 44', tone: 'loss' },
        { label: 'Word of mouth', sub: '19 leads', value: 63, text: '63%', tone: 'accent' },
      ],
      note: 'September leads are still being worked: shares compare across channels but run higher than the annual 16%. Junk is 160 of 420 losses; price is about a quarter, evenly across channels.',
    },
    {
      slot: 'findings',
      kind: 'columns',
      kicker: 'Calculator audit · price of the work',
      title: 'At a length boundary the price collapsed: 91 metres cheaper than 90',
      ask: 'Check the website calculators against real deals.',
      flag: '−₽34,654 for one extra metre',
      rows: [
        { label: '90 metres', value: 159176, text: '₽159,176', tone: 'base' },
        { label: '91 metres', value: 124522, text: '₽124,522', tone: 'loss' },
      ],
      note: 'And 100 metres came out cheaper than 90. Length boundaries moved into the settings and were checked in every calculator; the installation minimum was recalculated from cost, channel steel raised from ₽1,500 to ₽2,800 per metre.',
    },
    {
      slot: 'results',
      kind: 'compare',
      kicker: 'Prices from cost · since October 7',
      title: 'Margin per crew-day: 3D panels',
      ask: 'What do the new prices give on this year’s orders?',
      legend: ['Before', 'After'],
      scale: 'shared',
      rows: [
        { label: '10 metres', before: 7800, after: 18600, beforeText: '₽7,800', afterText: '₽18,600' },
        { label: '30 metres', before: 12300, after: 23000, beforeText: '₽12,300', afterText: '₽23,000' },
        { label: '100 metres', before: 16900, after: 21800, beforeText: '₽16,900', afterText: '₽21,800' },
      ],
      note: 'The bar is at least ₽20,000 per crew-day. Installation rates and minimums changed; material prices did not.',
    },
    {
      slot: 'results',
      kind: 'compare',
      kicker: 'Profit on the same orders · January–September · calculation',
      title: '+2.4% on price is +24% on profit',
      legend: ['Old', 'New'],
      rows: [
        { label: 'Left in the company', before: 13.7, after: 17.0, beforeText: '₽13.7M', afterText: '₽17.0M', delta: '+₽3.3M' },
      ],
      note: 'Profit is about a tenth of turnover, and the mark-up is shared with nobody and goes into it in full: hence the tenfold leverage. Calculated on actual orders with the same number of customers; to be checked against facts in spring 2027.',
    },
    {
      slot: 'results',
      kind: 'columns',
      kicker: 'Avito · end-to-end analytics down to the contract',
      title: 'Cost per contact fell almost threefold',
      ask: 'Does Avito pay off? Show the analytics down to the contract amount.',
      rows: [
        { label: 'July', value: 1095, text: '₽1,095' },
        { label: 'August', value: 716, text: '₽716' },
        { label: 'September', value: 382, text: '₽382' },
        { label: 'October 1–12', value: 569, text: '₽569', tone: 'base' },
      ],
      note: 'Spend per contract — ₽8,850 → ₽3,487, deals linked to listings — 11% → 52%. We don’t credit AI with the drop: the new bids are only prepared, and October has not matured.',
    },
    {
      slot: 'method',
      kind: 'verify',
      kicker: 'Financial check · January–September',
      title: 'The first figure was wrong',
      ask: 'The first estimate showed minus one and a half million. Is that true?',
      wrong: { label: 'First estimate · September 22', value: '−₽1.5M', text: 'Incomplete data: invoices stuck as drafts, the two entities’ costs never consolidated' },
      right: { label: 'Verified figure', value: '₽13.7M', text: 'left in the company for January–September, about ₽15.5M for 12 months — after the owner’s salary' },
      checksTitle: 'Four independent revenue sources',
      checks: ['Sales', 'Profit reports', 'Receipts', 'Purchases'],
      ok: 'matches',
      note: 'More than 40 exports from 1C and the CRM for the year. On dirty data AI gives a convincing and wrong answer: first check where the data lives, only then trust it.',
    },
    {
      slot: 'next',
      kind: 'ledger',
      kicker: 'Effect of the implementation',
      title: 'What is done — apart from what is calculated',
      ask: 'Sum up the effect. Separate what is done from calculations — no embellishment.',
      rows: [
        { tag: 'Done', tone: 'done', what: 'All 636 calculator options are at zero or above after all costs', amount: '—' },
        { tag: 'Calculation', tone: 'calc', what: 'The same January–September orders at the new prices', amount: '+₽3.3M over 9 months' },
        { tag: 'Calculation', tone: 'calc', what: 'November–December: long fences adjusted for short days instead of a loss of up to −13%', amount: '≈ +₽0.6M' },
        { tag: 'Potential', tone: 'potential', what: '“Avito lead → survey” from 12% to 20% at the same budget', amount: '≈ ×1.5 contracts' },
        { tag: 'Potential', tone: 'potential', what: '189 free crew-days — early booking for the season', amount: 'method to be checked' },
        { tag: '2027 goal', tone: 'goal', what: '10 crews by June', amount: '≈₽30M vs ₽15.5M' },
      ],
    },
  ],
}

/* ————————————————————— Производитель косметики ————————————————————— */

const COSMETICS: Visuals = {
  ru: [
    {
      slot: 'done',
      kind: 'systems',
      kicker: 'ИИ-контур на восемь человек',
      title: 'Каждый работает с данными компании напрямую',
      hub: 'ИИ-контур · 8 сотрудников',
      nodes: [
        { name: 'Wildberries и Ozon', does: 'Шесть кабинетов: продажи, реклама, остатки, отзывы', mode: 'прямой доступ', access: 'confirm' },
        { name: 'Аналитика маркетплейсов', does: 'Одна методика для всех периодов и кабинетов', mode: 'подключена', access: 'read' },
        { name: 'Учётная система', does: 'ИИ собирает и анализирует, решения принимают люди', mode: 'только чтение', access: 'read' },
        { name: 'Трекер задач', does: 'Задачи по находкам и автоматизациям', mode: 'подключён', access: 'read' },
        { name: 'Почта, диск, календарь', does: 'Рабочие документы и договорённости команды', mode: 'подключены', access: 'read' },
      ],
      rules: [
        'Каждое утро в 6:00 — сводка по шести кабинетам собственнику',
        'В плане было 3 автоматизации — команда запустила 26',
        'Приёмка — у операционного директора, а не у подрядчика',
      ],
    },
    {
      slot: 'findings',
      kind: 'tiles',
      kicker: 'Что ИИ нашёл в данных',
      title: 'Что годами не было видно',
      tiles: [
        { label: 'Доля в нише', value: '37,8 %', text: 'а в отчёте по рынку — 12,9 %: свой бренд числился конкурентом', meter: 37.8, tone: 'accent' },
        { label: 'Рост в штуках', value: '0', text: '59 тыс. единиц в месяц и год назад, и сейчас: весь рост выручки — цена' },
        { label: 'Маржа площадки', value: '26 → 14 %', text: 'за год, её доля в выручке — с 52 до 26 %', tone: 'loss' },
        { label: 'Декларации', value: '43 %', text: 'выручки — истекают в один день', meter: 43, tone: 'loss' },
        { label: 'Логистика за штуку', value: '98,6 ₽', text: 'против 62,2 ₽ на тех же товарах в соседнем кабинете', tone: 'loss' },
        { label: 'Оборудование в товаре', value: '4 млн ₽', text: 'стеллажи и станки искажают запасы и оборачиваемость' },
        { label: 'Мёртвый сток', value: '1 128 дней', text: 'запас одного товара' },
        { label: 'Свои против своих', value: '10 товаров', text: 'компании в одной рекламной выдаче' },
        { label: 'Ключ к финансам', value: 'в открытом доступе', text: 'лежал текстом в документе с общей ссылкой — отозван', tone: 'loss' },
      ],
    },
    {
      slot: 'results',
      kind: 'compare',
      kicker: 'Сентябрь (1–22) к июню · в день',
      title: 'Реклама стала работать в полтора раза лучше',
      legend: ['Июнь', 'Сент.'],
      rows: [
        { label: 'Выручка на 1 ₽ рекламы', before: 9.4, after: 14.5, beforeText: '9,4 ₽', afterText: '14,5 ₽', delta: '×1,5' },
        { label: 'Доля рекламы в выручке (ДРР)', before: 10.6, after: 6.9, beforeText: '10,6 %', afterText: '6,9 %', delta: '−3,7 п. п.' },
      ],
      note: 'Прибыль в день выросла на пятую часть при том, что комиссия площадок выросла на 11 процентных пунктов. Методика: база — июнь, сентябрь неполный, всё в расчёте на день.',
    },
    {
      slot: 'results',
      kind: 'compare',
      kicker: 'Рутина менеджеров · в день',
      title: 'Часы, которые вернулись команде',
      legend: ['Было', 'Стало'],
      rows: [
        { label: 'Контроль цен — у каждого из двух менеджеров', before: 120, after: 30, beforeText: '2 ч', afterText: '30 мин' },
        { label: 'Ответы на отзывы', before: 90, after: 10, beforeText: '1,5 ч', afterText: '10 мин' },
      ],
      note: 'Всего команда высвобождает около 270 часов в месяц — 114–120 тыс. ₽ по окладам. Часы — оценка самих сотрудников.',
    },
    {
      slot: 'notOurs',
      kind: 'ledger',
      kicker: 'Честная граница эффекта',
      title: 'Что дал ИИ-контур, а что — сезон и люди',
      rows: [
        { tag: 'Не ИИ', tone: 'other', what: 'Выручка +49 %, прибыль +20 % в день', amount: 'сезон, поставки, цены' },
        { tag: 'Не ИИ', tone: 'other', what: 'Комиссия площадок +11 п. п.', amount: 'решение площадок' },
        { tag: 'ИИ-контур', tone: 'done', what: 'Продажи и расходы — каждый день', amount: 'вместо 2 раз в неделю' },
        { tag: 'ИИ-контур', tone: 'done', what: 'Ответ о марже кабинета', amount: 'минуты вместо дней' },
        { tag: 'ИИ-контур', tone: 'done', what: 'Повторяющиеся потери найдены', amount: '≈200 тыс. ₽ в месяц' },
      ],
    },
  ],
  en: [
    {
      slot: 'done',
      kind: 'systems',
      kicker: 'An AI environment for eight people',
      title: 'Everyone works with the company’s data directly',
      hub: 'AI environment · 8 people',
      nodes: [
        { name: 'Wildberries & Ozon', does: 'Six seller accounts: sales, ads, stock, reviews', mode: 'direct access', access: 'confirm' },
        { name: 'Marketplace analytics', does: 'One method for all periods and accounts', mode: 'connected', access: 'read' },
        { name: 'Accounting system', does: 'AI gathers and analyses, people make the decisions', mode: 'read-only', access: 'read' },
        { name: 'Task tracker', does: 'Tasks for findings and automations', mode: 'connected', access: 'read' },
        { name: 'Email, drive, calendar', does: 'The team’s working documents and agreements', mode: 'connected', access: 'read' },
      ],
      rules: [
        'Every morning at 6:00 — a six-account summary for the owner',
        '3 automations were planned — the team launched 26',
        'Acceptance by the COO, not by a contractor',
      ],
    },
    {
      slot: 'findings',
      kind: 'tiles',
      kicker: 'What AI found in the data',
      title: 'What nobody had seen for years',
      tiles: [
        { label: 'Niche share', value: '37.8%', text: 'while the market report said 12.9%: the own brand was listed as a competitor', meter: 37.8, tone: 'accent' },
        { label: 'Unit growth', value: '0', text: '59 thousand units a month then and now: all revenue growth is price' },
        { label: 'Marketplace margin', value: '26 → 14%', text: 'in a year; its share of revenue fell from 52% to 26%', tone: 'loss' },
        { label: 'Certificates', value: '43%', text: 'of revenue — expire on the same day', meter: 43, tone: 'loss' },
        { label: 'Logistics per unit', value: '₽98.6', text: 'versus ₽62.2 for the same products in another account', tone: 'loss' },
        { label: 'Equipment as goods', value: '₽4M', text: 'racks and machines distort inventory and turnover' },
        { label: 'Dead stock', value: '1,128 days', text: 'of inventory for one item' },
        { label: 'Own vs own', value: '10 products', text: 'of the company in one ad search result' },
        { label: 'Finance key', value: 'in the open', text: 'sat as plain text in a link-shared document — revoked', tone: 'loss' },
      ],
    },
    {
      slot: 'results',
      kind: 'compare',
      kicker: 'September (1–22) vs June · per day',
      title: 'Advertising works one and a half times better',
      legend: ['June', 'Sept.'],
      rows: [
        { label: 'Revenue per ₽1 of advertising', before: 9.4, after: 14.5, beforeText: '₽9.4', afterText: '₽14.5', delta: '×1.5' },
        { label: 'Ad share of revenue', before: 10.6, after: 6.9, beforeText: '10.6%', afterText: '6.9%', delta: '−3.7 pp' },
      ],
      note: 'Daily profit grew by a fifth even though marketplace commission rose by 11 percentage points. Method: June baseline, September incomplete, everything per day.',
    },
    {
      slot: 'results',
      kind: 'compare',
      kicker: 'Managers’ routine · per day',
      title: 'Hours given back to the team',
      legend: ['Before', 'After'],
      rows: [
        { label: 'Price control — each of two managers', before: 120, after: 30, beforeText: '2 h', afterText: '30 min' },
        { label: 'Review replies', before: 90, after: 10, beforeText: '1.5 h', afterText: '10 min' },
      ],
      note: 'In total the team frees about 270 hours a month — ₽114–120K at salary rates. Hours are the employees’ own estimates.',
    },
    {
      slot: 'notOurs',
      kind: 'ledger',
      kicker: 'An honest boundary of the effect',
      title: 'What the AI environment gave — and what the season and people did',
      rows: [
        { tag: 'Not AI', tone: 'other', what: 'Revenue +49%, profit +20% per day', amount: 'season, supply, prices' },
        { tag: 'Not AI', tone: 'other', what: 'Marketplace commission +11 pp', amount: 'marketplaces’ decision' },
        { tag: 'AI environment', tone: 'done', what: 'Sales and costs — every day', amount: 'instead of twice a week' },
        { tag: 'AI environment', tone: 'done', what: 'An account’s margin answer', amount: 'minutes instead of days' },
        { tag: 'AI environment', tone: 'done', what: 'Recurring losses found', amount: '≈₽200K a month' },
      ],
    },
  ],
}

/* ————————————————————— Аренда авто для такси ————————————————————— */

const TRANSPORT: Visuals = {
  ru: [
    {
      slot: 'pointA',
      kind: 'transform',
      kicker: 'Технический блок',
      title: 'Ремонт начинался с разговора — теперь с записи',
      legend: ['Было', 'Стало'],
      rows: [
        { label: 'Заявка на ремонт', before: 'Учётной сущности не было: ремонт начинался с разговора', after: 'Заявка с историей машины и бюджетом по маркам и боксам' },
        { label: 'Смета сервиса', before: 'Технический директор согласовывал вручную, по памяти', after: 'Видно, что этой машине уже меняли, когда и за сколько' },
        { label: 'Гарантийная деталь сломалась', before: 'Сервис менял её ещё раз — за счёт компании', after: 'Система подсвечивает: меняем по гарантии' },
        { label: 'Вопросы водителей', before: 'Поддержка и продавцы', after: 'Приложение, которое команда написала сама: ≈70 % без человека' },
      ],
    },
    {
      slot: 'results',
      kind: 'waffle',
      kicker: 'Доля парка в ремонте',
      title: 'Из каждых 100 машин в ремонте стояли 14 — теперь меньше 6',
      caption: 'Одна клетка — одна машина из ста. Парк — около 400 машин.',
      grids: [
        { label: 'Было', value: 14, text: '14 % парка в ремонте', tone: 'loss' },
        { label: 'Стало', value: 5.7, text: '5,7 % парка в ремонте', tone: 'accent' },
      ],
    },
    {
      slot: 'results',
      kind: 'tiles',
      kicker: 'Технический блок · со слов технического директора',
      title: 'Ремонт дешевле и быстрее',
      tiles: [
        { label: 'Бюджет ремонта', value: '−25–30 %', text: 'технического, на машину' },
        { label: 'Время ремонта', value: '−65 %', text: 'среднее по парку' },
        { label: 'Закрытие смет', value: '−38 %', text: 'по времени' },
        { label: 'Цикл заявки', value: '×3', text: 'быстрее за 4 месяца' },
        { label: 'Срезано за июль', value: '≈1,15 млн ₽', text: 'работ, которые сервис заявил к оплате' },
        { label: 'Без человека', value: '≈70 %', text: 'вопросов водителей закрывает приложение', meter: 70, tone: 'accent' },
      ],
    },
    {
      slot: 'results',
      kind: 'compare',
      kicker: 'Фонд оплаты труда · данные финансового директора',
      title: 'Минус 500 тыс. ₽ в месяц — около 6 млн ₽ в год',
      legend: ['1 мая', 'После'],
      rows: [
        { label: 'Фонд оплаты труда в месяц', before: 2.5, after: 2.0, beforeText: '2,5 млн ₽', afterText: '2,0 млн ₽', delta: '−20 %' },
      ],
      note: 'Внедрение обошлось дешевле одного месяца этой экономии — и это без экономии на ремонте, которую ещё сверяем по документам.',
    },
  ],
  en: [
    {
      slot: 'pointA',
      kind: 'transform',
      kicker: 'Technical department',
      title: 'Repairs started with a conversation — now with a record',
      legend: ['Before', 'After'],
      rows: [
        { label: 'Repair request', before: 'No such record existed: a repair started with a conversation', after: 'A request with the car’s history and a budget by make and bay' },
        { label: 'Service estimate', before: 'The technical director approved it by hand, from memory', after: 'It shows what was already replaced on this car, when and for how much' },
        { label: 'A part under warranty fails', before: 'The service replaced it again — at the company’s expense', after: 'The system flags it: replace under warranty' },
        { label: 'Drivers’ questions', before: 'Support staff and sales', after: 'An app the team built itself: ≈70% without a human' },
      ],
    },
    {
      slot: 'results',
      kind: 'waffle',
      kicker: 'Share of the fleet in repair',
      title: 'Out of every 100 cars, 14 stood in repair — now fewer than 6',
      caption: 'One cell is one car out of a hundred. The fleet is about 400 cars.',
      grids: [
        { label: 'Before', value: 14, text: '14% of the fleet in repair', tone: 'loss' },
        { label: 'After', value: 5.7, text: '5.7% of the fleet in repair', tone: 'accent' },
      ],
    },
    {
      slot: 'results',
      kind: 'tiles',
      kicker: 'Technical department · per the technical director',
      title: 'Repairs got cheaper and faster',
      tiles: [
        { label: 'Repair budget', value: '−25–30%', text: 'technical, per car' },
        { label: 'Repair time', value: '−65%', text: 'fleet average' },
        { label: 'Closing estimates', value: '−38%', text: 'in time' },
        { label: 'Request cycle', value: '×3', text: 'faster in 4 months' },
        { label: 'Cut in July', value: '≈₽1.15M', text: 'of work the service billed' },
        { label: 'Without a human', value: '≈70%', text: 'of drivers’ questions closed by the app', meter: 70, tone: 'accent' },
      ],
    },
    {
      slot: 'results',
      kind: 'compare',
      kicker: 'Payroll · CFO’s data',
      title: 'Minus ₽500K a month — about ₽6M a year',
      legend: ['May 1', 'After'],
      rows: [
        { label: 'Monthly payroll', before: 2.5, after: 2.0, beforeText: '₽2.5M', afterText: '₽2.0M', delta: '−20%' },
      ],
      note: 'The rollout cost less than one month of these savings — not counting repair savings, which are still being checked against documents.',
    },
  ],
}

/* ————————————————————— Нефтегаз: производство и торговля ————————————————————— */

const NEFTEGAZ: Visuals = {
  ru: [
    {
      slot: 'pointA',
      kind: 'transform',
      kicker: 'Точка входа → 7 рабочих сессий',
      title: 'Учёт «для налогов» стал управленческим',
      legend: ['Было', 'Стало'],
      rows: [
        { label: 'Учёт', before: 'Только «для налогов», управленческого не было', after: 'Управленческий учёт собран и сверен бухгалтером: «состыковалось полностью»' },
        { label: '1С', before: 'На домашнем компьютере', after: 'На защищённом сервере — перенос силами ИИ' },
        { label: 'Клиентская база', before: 'Мёртвый груз в CRM', after: 'Дашборд, где ИИ работает в роли руководителя продаж' },
      ],
    },
    {
      slot: 'done',
      kind: 'ledger',
      kicker: 'Что не пришлось покупать',
      title: 'Функции закрыты внутри контура',
      rows: [
        { tag: 'Внутри', tone: 'done', what: 'Обслуживание учёта — подрядчики просили 20 000 ₽ в месяц или 2 000–3 000 ₽ в час', amount: 'не покупали' },
        { tag: 'Внутри', tone: 'done', what: 'Перенос 1С на защищённый сервер', amount: 'силами ИИ' },
        { tag: 'Внутри', tone: 'done', what: 'Дашборд продаж', amount: 'сотрудник собрал за 1 день' },
        { tag: 'Проверка', tone: 'calc', what: 'Роли собственника и менеджера', amount: '5 из 5' },
      ],
    },
  ],
  en: [
    {
      slot: 'pointA',
      kind: 'transform',
      kicker: 'Entry point → 7 working sessions',
      title: 'Books kept “for taxes” became management accounting',
      legend: ['Before', 'After'],
      rows: [
        { label: 'Accounting', before: 'Only “for taxes”, no management accounting', after: 'Management accounting assembled and checked by the accountant: “everything matched”' },
        { label: '1C', before: 'On a home computer', after: 'On a secured server — moved by AI' },
        { label: 'Client base', before: 'Dead weight in the CRM', after: 'A dashboard with AI acting as head of sales' },
      ],
    },
    {
      slot: 'done',
      kind: 'ledger',
      kicker: 'What did not have to be bought',
      title: 'Functions covered inside the environment',
      rows: [
        { tag: 'Inside', tone: 'done', what: 'Accounting support — contractors asked ₽20,000 a month or ₽2,000–3,000 an hour', amount: 'not bought' },
        { tag: 'Inside', tone: 'done', what: 'Moving 1C to a secured server', amount: 'done by AI' },
        { tag: 'Inside', tone: 'done', what: 'Sales dashboard', amount: 'built by an employee in 1 day' },
        { tag: 'Check', tone: 'calc', what: 'The owner’s and the manager’s roles', amount: '5 of 5' },
      ],
    },
  ],
}

/* ————————————————————— Кейсы до эпохи ИИ ————————————————————— */

const NOVAVI: Visuals = {
  ru: [
    {
      slot: 'pointA',
      kind: 'transform',
      kicker: 'Система управления',
      title: 'Из фрагментов — в систему',
      legend: ['Было', 'Стало'],
      rows: [
        { label: 'Управление', before: 'Фрагментарное: многих инструментов не хватало для роста', after: 'Стратегия, оргсхема, показатели, регламенты, финансы, делегирование' },
        { label: 'Команда', before: '15 человек', after: '40 человек по функциональным зонам — с оргсхемой и показателями' },
        { label: 'Обучение', before: 'Статья расходов', after: 'Внутренний продукт, приносящий прибыль' },
      ],
    },
    {
      slot: 'results',
      kind: 'compare',
      kicker: 'Точка Б · полтора года',
      title: 'Прибыль выросла быстрее выручки',
      legend: ['Было', 'Стало'],
      scale: 'shared',
      rows: [
        { label: 'Выручка в месяц', before: 90, after: 300, beforeText: '$90K', afterText: '$300K', delta: '×3,3' },
        { label: 'Чистая прибыль в месяц', before: 20, after: 110, beforeText: '$20K', afterText: '$110K', delta: '×5,5' },
      ],
      note: 'Система управления масштабирует маржу, а не только оборот: после пересборки юнит-экономики — 35 % маржинальности.',
    },
    {
      slot: 'results',
      kind: 'units',
      kicker: 'Команда',
      title: 'Из 15 человек — в 40, по функциональным зонам',
      groups: [
        { count: 15, label: 'человек на старте', tone: 'base' },
        { count: 40, label: 'человек через полтора года — у каждой зоны оргсхема и показатели', tone: 'accent' },
      ],
    },
  ],
  en: [
    {
      slot: 'pointA',
      kind: 'transform',
      kicker: 'Management system',
      title: 'From fragments to a system',
      legend: ['Before', 'After'],
      rows: [
        { label: 'Management', before: 'Fragmented: many tools needed for growth were missing', after: 'Strategy, org chart, metrics, policies, finance, delegation' },
        { label: 'Team', before: '15 people', after: '40 people by functional area — with an org chart and metrics' },
        { label: 'Training', before: 'A cost item', after: 'An internal product that makes a profit' },
      ],
    },
    {
      slot: 'results',
      kind: 'compare',
      kicker: 'Point B · a year and a half',
      title: 'Profit grew faster than revenue',
      legend: ['Before', 'After'],
      scale: 'shared',
      rows: [
        { label: 'Monthly revenue', before: 90, after: 300, beforeText: '$90K', afterText: '$300K', delta: '×3.3' },
        { label: 'Monthly net profit', before: 20, after: 110, beforeText: '$20K', afterText: '$110K', delta: '×5.5' },
      ],
      note: 'A management system scales margin, not just turnover: after the unit economics were rebuilt — a 35% margin.',
    },
    {
      slot: 'results',
      kind: 'units',
      kicker: 'Team',
      title: 'From 15 people to 40, by functional area',
      groups: [
        { count: 15, label: 'people at the start', tone: 'base' },
        { count: 40, label: 'people a year and a half later — every area with an org chart and metrics', tone: 'accent' },
      ],
    },
  ],
}

const ROOFMASTER: Visuals = {
  ru: [
    {
      slot: 'pointA',
      kind: 'transform',
      kicker: 'Собственник и компания',
      title: 'Собственник вышел из операционки — компания выросла',
      legend: ['Было', 'Стало'],
      rows: [
        { label: 'Операционка', before: 'Собственник — в ней каждый день', after: 'Её взял операционный директор; время собственника — на семью и развитие' },
        { label: 'Управление', before: 'Не было системы стратегического и операционного управления', after: 'Система, которая работает без ежедневного участия собственника' },
        { label: 'Пандемия', before: 'Впереди — нужно пройти её, сохранив команду и показатели', after: 'Финансовая подушка и диверсификация — ни одного сокращения' },
        { label: 'Рынки', before: 'Латвия', after: 'Латвия, Эстония, Норвегия — с локальными партнёрами' },
      ],
    },
    {
      slot: 'results',
      kind: 'bars',
      kicker: 'Точка Б · первый год',
      title: 'Прибыль выросла быстрее выручки',
      max: 2.5,
      rows: [
        { label: 'Выручка', value: 2, text: '×2', tone: 'base' },
        { label: 'Чистая прибыль', value: 2.5, text: '×2,5', tone: 'accent' },
      ],
      note: 'Рост — за первый год проекта, к уровню до его начала.',
    },
  ],
  en: [
    {
      slot: 'pointA',
      kind: 'transform',
      kicker: 'The owner and the company',
      title: 'The owner left day-to-day operations — the company grew',
      legend: ['Before', 'After'],
      rows: [
        { label: 'Operations', before: 'The owner — in them every day', after: 'Taken over by a COO; the owner’s time goes to family and growth' },
        { label: 'Management', before: 'No strategic or operational management system', after: 'A system that runs without the owner’s daily involvement' },
        { label: 'The pandemic', before: 'Ahead — to get through it keeping the team and the numbers', after: 'A financial cushion and diversification — zero layoffs' },
        { label: 'Markets', before: 'Latvia', after: 'Latvia, Estonia, Norway — with local partners' },
      ],
    },
    {
      slot: 'results',
      kind: 'bars',
      kicker: 'Point B · the first year',
      title: 'Profit grew faster than revenue',
      max: 2.5,
      rows: [
        { label: 'Revenue', value: 2, text: '×2', tone: 'base' },
        { label: 'Net profit', value: 2.5, text: '×2.5', tone: 'accent' },
      ],
      note: 'Growth over the first year of the project, against the level before it.',
    },
  ],
}

const DRIVEMAX: Visuals = {
  ru: [
    {
      slot: 'done',
      kind: 'timeline',
      kicker: 'Проект и после',
      title: 'Семь месяцев внедрения — год результата',
      steps: [
        { when: 'Старт', title: '$25M выручки', text: '5 магазинов под Бостоном, владелец в операционке' },
        { when: '7 месяцев', title: '+30 % выручки', text: '20 инструментов управления внедрено' },
        { when: '1 год', title: '$40M выручки', text: '×1,6 к старту; дивиденды собственника +40 %' },
        { when: '2026', title: 'Вернулись за ИИ', text: 'клиент пришёл за внедрением ИИ' },
      ],
    },
    {
      slot: 'results',
      kind: 'compare',
      kicker: 'Точка Б · за год',
      title: 'Выручка автодилера: $25M → $40M',
      legend: ['Было', 'Стало'],
      rows: [{ label: 'Выручка в год', before: 25, after: 40, beforeText: '$25M', afterText: '$40M', delta: '×1,6' }],
      note: 'Время собственника в операционке сократилось более чем вдвое: он управляет по регулярной отчётности и занимается стратегией.',
    },
    {
      slot: 'results',
      kind: 'transform',
      kicker: 'Управление',
      title: 'Владелец — из операционки в стратегию',
      legend: ['Было', 'Стало'],
      rows: [
        { label: 'Роль владельца', before: 'В операционном управлении', after: 'Стратегия и развитие; есть время на семью и путешествия' },
        { label: 'Управление', before: 'Системного управления не было', after: '20 инструментов: финансовое планирование, KPI в отделах продаж и сервисе, воронка в CRM, отчётность владельцу' },
      ],
    },
  ],
  en: [
    {
      slot: 'done',
      kind: 'timeline',
      kicker: 'The project and after',
      title: 'Seven months of rollout — a year of results',
      steps: [
        { when: 'Start', title: '$25M revenue', text: '5 stores near Boston, the owner in operations' },
        { when: '7 months', title: '+30% revenue', text: '20 management tools in place' },
        { when: '1 year', title: '$40M revenue', text: '×1.6 vs the start; owner dividends +40%' },
        { when: '2026', title: 'Back for AI', text: 'the client came back for an AI implementation' },
      ],
    },
    {
      slot: 'results',
      kind: 'compare',
      kicker: 'Point B · in a year',
      title: 'The dealer’s revenue: $25M → $40M',
      legend: ['Before', 'After'],
      rows: [{ label: 'Annual revenue', before: 25, after: 40, beforeText: '$25M', afterText: '$40M', delta: '×1.6' }],
      note: 'The owner’s time in operations fell by more than half: he manages by regular reporting and works on strategy.',
    },
    {
      slot: 'results',
      kind: 'transform',
      kicker: 'Management',
      title: 'The owner — from operations to strategy',
      legend: ['Before', 'After'],
      rows: [
        { label: 'Owner’s role', before: 'In day-to-day operations', after: 'Strategy and growth; time for family and travel' },
        { label: 'Management', before: 'There was no systematic management', after: '20 tools: financial planning, KPIs in sales and service, a CRM funnel, reporting to the owner' },
      ],
    },
  ],
}

const MARPOSADKABEL: Visuals = {
  ru: [
    {
      slot: 'results',
      kind: 'compare',
      kicker: 'Точка Б · пять лет',
      title: 'Выручка ×5 — и рентабельность выросла',
      legend: ['Было', 'Стало'],
      rows: [
        { label: 'Выручка за год', before: 400, after: 2000, beforeText: '400 млн ₽', afterText: '2+ млрд ₽', delta: '×5' },
        { label: 'Рентабельность', before: 7, after: 9, beforeText: '7 %', afterText: '9 % — целевая', delta: '+2 п. п.' },
      ],
      note: 'Рентабельность выросла, несмотря на масштабирование в пять раз. Опора — сильный финансовый блок: регулярное бюджетирование и контроллинг.',
    },
    {
      slot: 'done',
      kind: 'transform',
      kicker: 'Оргструктура',
      title: 'Семейное предприятие стало бизнесом, которым управляет команда',
      legend: ['Было', 'Стало'],
      rows: [
        { label: 'Ключевые позиции', before: 'Без управленческой команды', after: 'Генеральный директор, коммерческий директор, руководитель продаж' },
        { label: 'Структура', before: 'Семейное предприятие', after: 'Бизнес, которым управляет команда, с сильным финансовым блоком: бюджетирование и контроллинг' },
        { label: 'Производство', before: '400 млн ₽ выручки на текущей клиентской базе', after: 'Расширены производство и продуктовая линейка' },
      ],
    },
  ],
  en: [
    {
      slot: 'results',
      kind: 'compare',
      kicker: 'Point B · five years',
      title: 'Revenue ×5 — and profitability rose',
      legend: ['Before', 'After'],
      rows: [
        { label: 'Annual revenue', before: 400, after: 2000, beforeText: '₽400M', afterText: '₽2B+', delta: '×5' },
        { label: 'Profitability', before: 7, after: 9, beforeText: '7%', afterText: '9% — target', delta: '+2 pp' },
      ],
      note: 'Profitability rose despite scaling fivefold. The backbone is a strong finance function: regular budgeting and controlling.',
    },
    {
      slot: 'done',
      kind: 'transform',
      kicker: 'Organization',
      title: 'A family business became one run by a management team',
      legend: ['Before', 'After'],
      rows: [
        { label: 'Key positions', before: 'No management team', after: 'CEO, commercial director, head of sales' },
        { label: 'Structure', before: 'A family business', after: 'A business run by a team, with a strong finance function: budgeting and controlling' },
        { label: 'Production', before: '₽400M of revenue on the existing client base', after: 'Production and the product range expanded' },
      ],
    },
  ],
}

const KARATE_SUSHI: Visuals = {
  ru: [
    {
      slot: 'done',
      kind: 'timeline',
      kicker: 'Путь сети · 5 лет',
      title: 'От своих ресторанов — к франшизе в прибыли',
      steps: [
        { when: 'Шаг 1', title: 'Системное управление', text: '20 инструментов по всем функциям' },
        { when: 'Шаг 2', title: 'Стандарты', text: 'закупка, кухня, подача, сервис' },
        { when: 'Шаг 3', title: 'Управляющие', text: 'выходят на стандарт по системе обучения' },
        { when: 'Шаг 4', title: 'Франшиза', text: 'от бренд-бука до модели роялти' },
        { when: 'Шаг 5', title: 'Сопровождение', text: 'каждого франчайзи — до безубыточности' },
      ],
    },
    {
      slot: 'results',
      kind: 'units',
      kicker: 'Франчайзи',
      title: 'Франшизы не было — теперь 8 франчайзи, и все в прибыли',
      groups: [{ count: 8, label: 'франчайзи — все в чистой прибыли; на старте было 0', tone: 'accent' }],
      note: 'Выручка сети за пять лет выросла в 10 раз.',
    },
  ],
  en: [
    {
      slot: 'done',
      kind: 'timeline',
      kicker: 'The chain’s path · 5 years',
      title: 'From own restaurants to a profitable franchise',
      steps: [
        { when: 'Step 1', title: 'Systematic management', text: '20 tools across all functions' },
        { when: 'Step 2', title: 'Standards', text: 'purchasing, kitchen, serving, service' },
        { when: 'Step 3', title: 'Managers', text: 'reach the standard through a training system' },
        { when: 'Step 4', title: 'Franchise', text: 'from brand book to the royalty model' },
        { when: 'Step 5', title: 'Support', text: 'every franchisee — to break-even' },
      ],
    },
    {
      slot: 'results',
      kind: 'units',
      kicker: 'Franchisees',
      title: 'There was no franchise — now 8 franchisees, all profitable',
      groups: [{ count: 8, label: 'franchisees — all in net profit; there were 0 at the start', tone: 'accent' }],
      note: 'The chain’s revenue grew tenfold in five years.',
    },
  ],
}

const CRYPTO: Visuals = {
  ru: [
    {
      slot: 'pointA',
      kind: 'units',
      kicker: 'Оргсхема',
      title: '120 сотрудников — у каждого роль, результат и показатель',
      groups: [{ count: 120, label: 'сотрудников: для каждого согласован целевой конечный результат и KPI', tone: 'accent' }],
      note: 'До проекта не было ни должностных инструкций, ни понятных зон ответственности.',
    },
    {
      slot: 'results',
      kind: 'compare',
      kicker: 'Точка Б · 3,5 месяца',
      title: 'Чистая прибыль: $4M → $9M в месяц',
      legend: ['Было', 'Стало'],
      rows: [{ label: 'Чистая прибыль в месяц', before: 4, after: 9, beforeText: '$4M', afterText: '$9M', delta: '×2,25' }],
    },
    {
      slot: 'results',
      kind: 'transform',
      kicker: 'Управление',
      title: 'От хаоса — к управляемой организации',
      legend: ['Было', 'Стало'],
      rows: [
        { label: 'Подбор', before: 'Отдела подбора и адаптации не было', after: 'Запущен с нуля и описан регламентами: вакансия закрывается за 2 недели' },
        { label: 'Маркетинг', before: 'Хаотичный, без руководителя', after: 'Когортный, со стратегией привлечения' },
        { label: 'Процессы', before: 'Нет зон ответственности и конечных результатов', after: 'Процессы выстроены; с теми, кто не давал результата, компания рассталась' },
      ],
    },
  ],
  en: [
    {
      slot: 'pointA',
      kind: 'units',
      kicker: 'Org chart',
      title: '120 employees — each with a role, a result and a metric',
      groups: [{ count: 120, label: 'employees: each agreed a target end result and KPIs', tone: 'accent' }],
      note: 'Before the project there were no job descriptions and no clear areas of responsibility.',
    },
    {
      slot: 'results',
      kind: 'compare',
      kicker: 'Point B · 3.5 months',
      title: 'Net profit: $4M → $9M a month',
      legend: ['Before', 'After'],
      rows: [{ label: 'Monthly net profit', before: 4, after: 9, beforeText: '$4M', afterText: '$9M', delta: '×2.25' }],
    },
    {
      slot: 'results',
      kind: 'transform',
      kicker: 'Management',
      title: 'From chaos to a managed organization',
      legend: ['Before', 'After'],
      rows: [
        { label: 'Recruiting', before: 'No recruiting and onboarding team', after: 'Built from scratch with policies: a vacancy closes in 2 weeks' },
        { label: 'Marketing', before: 'Chaotic, no head of marketing', after: 'Cohort-based, with an acquisition strategy' },
        { label: 'Processes', before: 'No areas of responsibility or end results', after: 'Processes in place; the company parted with those who delivered no results' },
      ],
    },
  ],
}

export const CASE_VISUALS: Record<CaseId, Visuals> = {
  fences: FENCES,
  cosmetics: COSMETICS,
  transport: TRANSPORT,
  'neftegaz-trade': NEFTEGAZ,
  novavi: NOVAVI,
  roofmaster: ROOFMASTER,
  drivemax: DRIVEMAX,
  marposadkabel: MARPOSADKABEL,
  'karate-sushi': KARATE_SUSHI,
  'crypto-exchange': CRYPTO,
}

const RU_TYPESET = Object.fromEntries(
  Object.entries(CASE_VISUALS).map(([id, v]) => [id, { ru: nbspDeep(v.ru), en: v.en }]),
) as Record<CaseId, Visuals>

/** Иллюстрации кейса для локали; в русских — неразрывные пробелы. */
export function caseVisuals(id: CaseId, locale: Locale): CaseVisual[] {
  return RU_TYPESET[id]?.[locale] ?? []
}
